import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
    const navigate = useNavigate();
    const [marketplaceSearch, setMarketplaceSearch] = useState('');

    const handleMarketplaceSearch = (event) => {
        event.preventDefault();
        const queryText = marketplaceSearch.trim();
        navigate(queryText ? `/marketplace?search=${encodeURIComponent(queryText)}` : '/marketplace');
    };

    return (
        <div className="home-page">
            <section className="home-hero">
                <div className="home-hero-overlay">
                    <div className="home-hero-content">
                        <p className="home-eyebrow">Tune marketplace</p>
                        <h1>Find a beat you can trust. License it in minutes.</h1>
                        <p className="home-hero-copy">
                            Tune helps artists discover release-ready beats with clear pricing, simple licenses, and instant access after purchase.
                        </p>
                        <form className="home-search" onSubmit={handleMarketplaceSearch}>
                            <svg className="home-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="search"
                                value={marketplaceSearch}
                                onChange={(event) => setMarketplaceSearch(event.target.value)}
                                placeholder="Search trap drums, R&B loops, producer names..."
                                aria-label="Search the marketplace"
                            />
                            <button type="submit">Search</button>
                        </form>
                        <div className="home-hero-actions">
                            <Link to="/marketplace" className="home-button home-button-primary">
                                Browse Beats
                            </Link>
                            <Link to="/upload" className="home-button home-button-secondary">
                                Start Selling
                            </Link>
                        </div>
                        <div className="home-hero-stats" aria-label="Marketplace highlights">
                            <div>
                                <strong>95%</strong>
                                <span>producer balance</span>
                            </div>
                            <div>
                                <strong>$0</strong>
                                <span>buyer platform fee</span>
                            </div>
                            <div>
                                <strong>Instant</strong>
                                <span>library delivery</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="home-proof-strip" aria-label="Tune promises">
                <div>
                    <strong>$0 buyer fee marketplace</strong>
                    <span>Artists see the beat price before checkout and avoid surprise platform add-ons.</span>
                </div>
                <div>
                    <strong>Plain-English licensing</strong>
                    <span>Usage limits, files, and release rules stay visible before purchase.</span>
                </div>
                <div>
                    <strong>Producer storefronts</strong>
                    <span>Creators get a polished page for their catalog, brand, and sales.</span>
                </div>
            </section>

            <section className="home-section home-workflow">
                <div className="home-section-heading">
                    <p className="home-eyebrow">Simple buyer flow</p>
                    <h2>Browse, license, and keep moving.</h2>
                    <p>Browse with confidence, understand the license, and keep every purchase organized.</p>
                </div>
                <div className="home-step-grid">
                    <article>
                        <span>01</span>
                        <h3>Search by sound</h3>
                        <p>Find the right track by vibe, producer, genre, or mood.</p>
                    </article>
                    <article>
                        <span>02</span>
                        <h3>Preview the license</h3>
                        <p>Each listing should show price, files, streams, and usage limits before money moves.</p>
                    </article>
                    <article>
                        <span>03</span>
                        <h3>Save to Library</h3>
                        <p>Purchases, downloads, and license details belong in one quiet place after checkout.</p>
                    </article>
                </div>
            </section>

            <section className="home-section home-producer-panel">
                <div className="home-producer-copy">
                    <p className="home-eyebrow">For producers</p>
                    <h2>Turn your beat catalog into a real storefront.</h2>
                    <p>
                        Upload tracks, present your brand, set clear license terms, and give artists a smooth way to buy from you.
                    </p>
                    <Link to="/upload" className="home-button home-button-primary">
                        Open Sell Beats
                    </Link>
                </div>
                <div className="home-tool-list" aria-label="Producer tools">
                    <div>
                        <strong>Storefront</strong>
                        <span>Profile, cover image, bio, catalog, and public links.</span>
                    </div>
                    <div>
                        <strong>Upload flow</strong>
                        <span>Artwork, audio, tags, pricing, and license defaults.</span>
                    </div>
                    <div>
                        <strong>Analytics</strong>
                        <span>Views, plays, sales, followers, and conversion signals.</span>
                    </div>
                    <div>
                        <strong>Earnings</strong>
                        <span>Simple seller-side money tracking without confusing buyer fees.</span>
                    </div>
                </div>
            </section>

            <section className="home-section home-value-section">
                <div className="home-section-heading">
                    <p className="home-eyebrow">Why Tune</p>
                    <h2>A marketplace built around trust at checkout.</h2>
                    <p>From first preview to final download, Tune keeps the buying experience clear and the selling experience professional.</p>
                </div>
                <div className="home-focus-grid">
                    <article>
                        <h3>Clear prices</h3>
                        <p>Artists know what a beat costs before checkout, with no confusing buyer fees added at the end.</p>
                    </article>
                    <article>
                        <h3>License confidence</h3>
                        <p>Usage rights, included files, and release limits are easy to understand before a purchase.</p>
                    </article>
                    <article>
                        <h3>Fast delivery</h3>
                        <p>Purchased beats and license records stay available in the buyer's library.</p>
                    </article>
                    <article>
                        <h3>Better storefronts</h3>
                        <p>Producers can show their sound, organize their catalog, and build a cleaner buyer experience.</p>
                    </article>
                </div>
            </section>

            <section className="home-final-cta">
                <div>
                    <p className="home-eyebrow">Start creating</p>
                    <h2>Find your next sound or start selling your catalog today.</h2>
                </div>
                <div className="home-final-actions">
                    <Link to="/marketplace" className="home-button home-button-primary">
                        Browse Beats
                    </Link>
                    <Link to="/library" className="home-button home-button-secondary">
                        View Library
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
